import Axios, { type AxiosError } from 'axios'

/**
 * Transport-level request failures — aborted CORS preflight, dropped connection,
 * DNS hiccup — reach the response interceptor as a bare `Network Error` with no
 * response attached.  They are indistinguishable from being offline: a failed
 * OPTIONS preflight is browser-internal and never surfaces to JS, so all the app
 * ever sees is the actual request failing.
 *
 * Escalating the FIRST such failure straight to the global backend-unavailable
 * state meant one flaky preflight for a decorative thumbnail popped the
 * persistent ShowError.vue dialog and blocked the whole app (AAR-1667).  So the
 * first failure stays quiet and is left to the caller's own catch; only a second
 * one inside this window escalates.  Reads go through TanStack Query with the
 * default `retry: 3`, so a genuine outage produces its second failure about a
 * second later and the dialog still appears.
 */
export const TRANSPORT_FAILURE_WINDOW_MS = 10_000

/** `null` = no failure recorded yet, which is NOT the same as "at timestamp 0". */
let lastTransportFailureAt: number | null = null

/**
 * True only for rejections that would actually drive the global escalation in
 * responseOnRejected — no response, not a cancel, and the exact `Network Error`
 * message that gets rewritten to a synthetic 500 there.
 *
 * Deliberately narrower than "any transport failure": axios reports timeouts as
 * `ECONNABORTED` with `timeout of Xms exceeded` and browser aborts as
 * `Request aborted`, and neither can reach the escalation branch.  Counting them
 * would let one timeout make the next genuine network error escalate on its
 * first occurrence — exactly the bug this module exists to prevent.  Keep this
 * predicate in lockstep with the `error_info.message === 'Network Error'`
 * condition in interceptors.ts.
 */
export function isEscalatableNetworkFailure(result: AxiosError): boolean {
	return (
		!result.response &&
		!Axios.isCancel(result) &&
		result.message === 'Network Error'
	)
}

/**
 * Records a network failure and reports whether it is the first one in the
 * current window — i.e. whether it should stay silent instead of escalating.
 *
 * Uses the monotonic clock so a system time adjustment cannot turn the elapsed
 * comparison negative and escalate a first failure.
 */
export function recordTransportFailure(now: number = performance.now()): {
	isFirstInWindow: boolean
} {
	const isFirstInWindow =
		lastTransportFailureAt === null ||
		now - lastTransportFailureAt > TRANSPORT_FAILURE_WINDOW_MS
	lastTransportFailureAt = now

	return { isFirstInWindow }
}

/**
 * A successful response proves the API is reachable, so an earlier blip must not
 * pair up with a much later one to fake an outage.
 */
export function resetTransportFailures(): void {
	lastTransportFailureAt = null
}
